import { Popover } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconChevronDown,
  IconInbox,
  IconSend,
  IconSparkles,
  IconTruck,
  IconUser,
} from '@tabler/icons-react';
import { useLanguage } from '../../i18n/LanguageProvider';
import { useOrgContext } from '../org/OrgProvider';
import { useAppSwitcherContext } from '../AppSwitcherProvider';
import { useSidebarContext } from './SidebarProvider';
import { COLOR_MAP } from '../../utils/color';
import { DabsiApp } from '../../types/App';
import sidebarClasses from './Sidebar.module.css';

const APP_ICONS: Record<string, React.ReactNode> = {
  clientell: <IconUser size={18} />,
  imminote: <IconSend size={18} />,
  botmaker: <IconSparkles size={18} />,
  rakine: <IconTruck size={18} />,
  whatchat: <IconInbox size={18} />,
};

export const SidebarAppSwitcher = () => {
  const { messages } = useLanguage();
  const { app, setApp } = useAppSwitcherContext();
  const { org, orgApps } = useOrgContext();
  const { expanded, closeMobile } = useSidebarContext();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  const appDetails = useMemo(
    () => ({
      clientell: {
        name: messages.header.clientell,
        description: messages.header.clientellDescription,
      },
      imminote: {
        name: messages.header.imminote,
        description: messages.header.imminoteDescription,
      },
      botmaker: {
        name: messages.header.botmaker,
        description: messages.header.botmakerDescription,
      },
      rakine: {
        name: messages.header.rakine,
        description: messages.header.rakineDescription,
      },
      whatchat: {
        name: messages.header.whatchat,
        description: messages.header.whatchatDescription,
      },
    }),
    [messages]
  );

  const handleSwitch = (newApp: DabsiApp) => {
    setApp(newApp);
    localStorage.setItem('selectedApp', newApp);
    if (org) navigate(`/${newApp}/orgs/${org.id}`);
    setOpened(false);
    closeMobile();
  };

  const current = appDetails[app];
  const color = COLOR_MAP[app];

  return (
    <div style={{ padding: '12px' }}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width={220}
        radius="md"
        shadow="lg"
      >
        <Popover.Target>
          <button
            type="button"
            onClick={() => setOpened(o => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: expanded ? 'flex-start' : 'center',
              gap: expanded ? 10 : 0,
              width: '100%',
              padding: expanded ? '10px 12px' : '8px',
              border: '1px solid #eef0f6',
              borderRadius: 10,
              background: '#fafbfc',
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'border-color 0.15s ease',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {APP_ICONS[app]}
            </div>
            {expanded && (
              <>
                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#0f172a',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {current?.name}
                  </div>
                </div>
                <IconChevronDown
                  size={14}
                  style={{
                    color: '#94a3b8',
                    flexShrink: 0,
                    transition: 'transform 0.15s',
                    transform: opened ? 'rotate(180deg)' : 'none',
                  }}
                />
              </>
            )}
          </button>
        </Popover.Target>

        <Popover.Dropdown style={{ padding: 6 }}>
          {orgApps.map(appKey => {
            const details = appDetails[appKey];
            if (!details) return null;
            const isActive = appKey === app;
            return (
              <button
                key={appKey}
                type="button"
                onClick={() => handleSwitch(appKey)}
                className={!isActive ? sidebarClasses.hoverItem : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 8,
                  background: isActive ? '#f8f6ff' : 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    backgroundColor: COLOR_MAP[appKey],
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {APP_ICONS[appKey]}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}
                  >
                    {details.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {details.description}
                  </div>
                </div>
              </button>
            );
          })}
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};
